/**
 * One search result row (search-result-disclosure 03).
 *
 * Level 1 is the compact row: thumbnail, name, breadcrumb, description and
 * chips. Clicking the row expands level 2 in place (owner/type/tags, preview
 * strip, contents). The trailing right arrow is the direct open — it never
 * expands. Level 2's Preview opens the overlay pane (level 3).
 */

import { useState, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ChevronRightIcon } from "../../shared/components/icons";
import {
  CONTAINER_DEADLINE_VOCABULARY,
  DeadlineStatusDot,
  ITEM_DEADLINE_VOCABULARY,
} from "../../shared/components/Deadlines";
import { useT } from "../../shared/i18n";
import type { SearchDisplayState } from "./facets";
import { HitMetaChips } from "./HitBits";
import { HitLevelTwo } from "./HitLevelTwo";
import { HitThumb } from "./ResultMedia";
import { type SearchHit } from "./searchHits";

export function ResultRow({
  hit,
  display,
  onOpen,
  onPreview,
  onOpenPlacement,
  onOpenContainer,
  quantity,
  canWrite = true,
  onQuantityChange,
  identity,
  bulkMode = false,
  selected = false,
  onSelect,
  trailing,
  hideDeadlineDot = false,
}: {
  hit: SearchHit;
  display: SearchDisplayState;
  onOpen: () => void;
  onPreview: () => void;
  /** Open one of a multi-placed item's hosts (search rows only). */
  onOpenPlacement?: (containerId: string) => void;
  /** Link to the row's container; the item list only passes it for one placement. */
  onOpenContainer?: () => void;
  /** Current placement quantity (optimistic value wins over the hit's mirror). */
  quantity?: number;
  /** False for a read-only share: the link stays, the stepper hides. */
  canWrite?: boolean;
  onQuantityChange?: (next: number) => void;
  /** Override the identity line (the item list shows the item, not its host). */
  identity?: { uuid: string; name: string };
  bulkMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Optional trailing slot (the opt-in deadline column). */
  trailing?: ReactNode;
  hideDeadlineDot?: boolean;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const isItem = hit.result.type === "item";
  const vocabulary = isItem ? ITEM_DEADLINE_VOCABULARY : CONTAINER_DEADLINE_VOCABULARY;
  const trail = hit.noPlacement ? t("searchUnplaced") : hit.trail;
  const showLevelTwo = expanded && !bulkMode;
  const placementCount = isItem ? hit.placements.length : 0;
  const showPlacementList = placementCount > 1 && onOpenPlacement !== undefined;

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }} data-testid="search-hit">
      <Box sx={{ display: "flex", alignItems: "flex-start", minHeight: 56 }}>
        {bulkMode && (
          <Box sx={{ pt: 1.5, pl: 1 }}>
            <Checkbox checked={selected} tabIndex={-1} disableRipple />
          </Box>
        )}
        <ButtonBase
          aria-expanded={bulkMode ? undefined : expanded}
          onClick={() => {
            if (bulkMode) onSelect?.();
            else setExpanded((current) => !current);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            p: 1,
            textAlign: "left",
          }}
        >
          {display.previews && hit.photos[0] && (
            <Box sx={{ width: 44, flexShrink: 0 }}>
              <HitThumb photo={hit.photos[0]} size={44} />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} component="span">
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {hit.name}
              </Typography>
              {!hideDeadlineDot && (
                <DeadlineStatusDot properties={hit.properties} vocabulary={vocabulary} />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", minWidth: 0 }}
            >
              {trail}
            </Typography>
            {showPlacementList && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("searchInContainers", { count: String(placementCount) })}
              </Typography>
            )}
            {display.descriptions && hit.description !== "" && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {hit.description}
              </Typography>
            )}
            <HitMetaChips hit={hit} />
          </Box>
        </ButtonBase>
        {trailing !== undefined && <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
        <IconButton
          aria-label={`${t("searchOpen")} ${hit.name}`}
          onClick={onOpen}
          sx={{ mt: 0.5, mr: 0.5 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {showLevelTwo && (
        <Box sx={{ px: 1, pb: 1.25 }}>
          <HitLevelTwo
            hit={hit}
            display={display}
            onOpenPlacement={onOpenPlacement}
            onOpenContainer={onOpenContainer}
            quantity={quantity}
            canWrite={canWrite}
            onQuantityChange={onQuantityChange}
            identity={identity}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button variant="outlined" size="small" onClick={onPreview}>
              {t("preview")}
            </Button>
            <Button size="small" onClick={() => setExpanded(false)}>
              {t("searchLess")}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
